import { useState, type FC } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@ui/dialog.tsx';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { useApiAdminsalesHierarchiesPost } from '@r/api/generated/client.ts';
import type { SalesHierarchySalesHierarchyReadModel } from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

interface CreateHierarchyDialogProps {
  onCreated: (hierarchy: SalesHierarchySalesHierarchyReadModel) => void;
}

/** Modale de création d'une hiérarchie commerciale (nom seul), avec le bouton déclencheur. */
export const CreateHierarchyDialog: FC<CreateHierarchyDialogProps> = ({ onCreated }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const showOrvalErrorToast = useOrvalErrorToast();

  // Défini dans le composant (pas au module scope) pour avoir accès à `t` : message d'erreur traduit plutôt que le défaut anglais de Zod.
  const FormSchema = z.object({
    name: z.string().min(1, t('form.fields.required'))
  });

  type FormValues = z.infer<typeof FormSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { name: '' }
  });

  const createHierarchyMutation = useApiAdminsalesHierarchiesPost({
    mutation: {
      onSuccess: (hierarchy) => {
        toast.success(t('admin.hierarchy.create.success'));
        form.reset();
        setIsOpen(false);
        onCreated(hierarchy);
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'post', '/admin/sales-hierarchies')) {
          showOrvalErrorToast(error, t('admin.hierarchy.create.error'));
        } else {
          toast.error(t('admin.hierarchy.create.error'));
        }
      }
    }
  });

  function onSubmit(data: FormValues) {
    createHierarchyMutation.mutate({ data: { name: data.name } });
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button rounded="full">
          {t('admin.hierarchy.create.cta')} <Plus />
        </Button>
      </DialogTrigger>

      <DialogContent className="p-10 !rounded-3xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('admin.hierarchy.create.dialog.title')}</DialogTitle>
          <DialogDescription>{t('admin.hierarchy.create.dialog.description')}</DialogDescription>
        </DialogHeader>

        <Form {...form} schema={FormSchema}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('admin.hierarchy.form.name.label')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('admin.hierarchy.form.name.placeholder')}
                      {...field}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              rounded="full"
              loading={createHierarchyMutation.isPending}
              className="w-full"
              type="submit"
            >
              {t('admin.hierarchy.form.submit')}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
